import type { BrandDashboardPeriod } from "@/state/store/userPreferences";

export type DisplayPeriodGranularity = "day" | "week" | "month";

export interface DisplayPeriodConfig {
  value: BrandDashboardPeriod;
  label: string;
  pastLabel: string;
  rangeDays: number;
  granularity: DisplayPeriodGranularity;
}

export interface DisplayPeriodTimePoint {
  date: string;
  label: string;
  intervalLabel: string;
  start: Date;
  endExclusive: Date;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const UTC_SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
const GRANULARITY_DAYS: Record<DisplayPeriodGranularity, number> = {
  day: 1,
  week: 7,
  month: 30,
};

const DEFAULT_BUCKET_ANCHOR: Record<DisplayPeriodGranularity, "start" | "end"> = {
  day: "start",
  week: "end",
  month: "start",
};

export const DISPLAY_PERIOD_CONFIGS: DisplayPeriodConfig[] = [
  { value: "7d", label: "7 Days", pastLabel: "Past 7 days", rangeDays: 7, granularity: "day" },
  { value: "14d", label: "14 Days", pastLabel: "Past 14 days", rangeDays: 14, granularity: "day" },
  { value: "30d", label: "30 Days", pastLabel: "Past 30 days", rangeDays: 30, granularity: "week" },
  { value: "90d", label: "90 Days", pastLabel: "Past 90 days", rangeDays: 90, granularity: "week" },
  { value: "180d", label: "180 Days", pastLabel: "Past 180 days", rangeDays: 180, granularity: "month" },
];

const DISPLAY_PERIOD_CONFIG_BY_VALUE = Object.fromEntries(
  DISPLAY_PERIOD_CONFIGS.map((config) => [config.value, config]),
) as Record<BrandDashboardPeriod, DisplayPeriodConfig>;

export const DISPLAY_PERIOD_OPTIONS = DISPLAY_PERIOD_CONFIGS.map((config) => ({
  value: config.value,
  label: config.label,
}));

export const getDisplayPeriodConfig = (period: BrandDashboardPeriod): DisplayPeriodConfig =>
  DISPLAY_PERIOD_CONFIG_BY_VALUE[period];

export const getDisplayPeriodPastLabel = (period: BrandDashboardPeriod) =>
  getDisplayPeriodConfig(period).pastLabel;

export const getDisplayPeriodQuery = (period: BrandDashboardPeriod) => {
  const config = getDisplayPeriodConfig(period);
  return {
    rangeDays: config.rangeDays,
    granularity: config.granularity,
  };
};

export const getDisplayPeriodBucketSizeDays = (period: BrandDashboardPeriod) =>
  GRANULARITY_DAYS[getDisplayPeriodConfig(period).granularity];

export const getDisplayPeriodDateRange = (period: BrandDashboardPeriod, referenceDate = new Date()) => {
  const config = getDisplayPeriodConfig(period);
  const endExclusive = addUtcDays(startOfUtcDay(referenceDate), 1);
  const start = addUtcDays(endExclusive, -config.rangeDays);

  return {
    start,
    endExclusive,
    startDate: toUtcDateKey(start),
    endDate: toUtcDateKey(addUtcDays(endExclusive, -1)),
  };
};

export const getDisplayPeriodBucketStart = (
  period: BrandDashboardPeriod,
  date: Date,
  referenceDate = new Date(),
) => {
  const normalized = startOfUtcDay(date);
  const buckets = buildDisplayPeriodBuckets(period, referenceDate);
  const firstBucket = buckets[0];
  const lastBucket = buckets[buckets.length - 1];

  if (!firstBucket || !lastBucket) {
    throw new Error(`Display period ${period} produced no time buckets.`);
  }

  for (const bucket of buckets) {
    if (normalized.getTime() >= bucket.start.getTime() && normalized.getTime() < bucket.endExclusive.getTime()) {
      return bucket;
    }
  }

  if (normalized.getTime() < firstBucket.start.getTime()) {
    return firstBucket;
  }

  return lastBucket;
};

export const getDisplayPeriodTimePoints = (
  period: BrandDashboardPeriod,
  referenceDate = new Date(),
): DisplayPeriodTimePoint[] => {
  return buildDisplayPeriodBuckets(period, referenceDate);
};

function startOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function addUtcDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

function toUtcDateKey(date: Date): string {
  return startOfUtcDay(date).toISOString().slice(0, 10);
}

function buildDisplayPeriodBuckets(period: BrandDashboardPeriod, referenceDate: Date): DisplayPeriodTimePoint[] {
  const config = getDisplayPeriodConfig(period);
  const { start, endExclusive } = getDisplayPeriodDateRange(period, referenceDate);
  const bucketSizeDays = GRANULARITY_DAYS[config.granularity];
  const bucketCount = Math.ceil(config.rangeDays / bucketSizeDays);
  const bucketAnchor = DEFAULT_BUCKET_ANCHOR[config.granularity];
  const buckets: DisplayPeriodTimePoint[] = [];

  for (let index = 0; index < bucketCount; index += 1) {
    const nominalStart =
      bucketAnchor === "end"
        ? addUtcDays(endExclusive, -(index + 1) * bucketSizeDays)
        : addUtcDays(start, index * bucketSizeDays);
    const nominalEndExclusive =
      bucketAnchor === "end"
        ? addUtcDays(endExclusive, -index * bucketSizeDays)
        : addUtcDays(nominalStart, bucketSizeDays);
    const bucketStart = new Date(Math.max(nominalStart.getTime(), start.getTime()));
    const bucketEndExclusive = new Date(Math.min(nominalEndExclusive.getTime(), endExclusive.getTime()));

    if (bucketStart.getTime() >= bucketEndExclusive.getTime()) {
      continue;
    }

    buckets.push({
      start: bucketStart,
      endExclusive: bucketEndExclusive,
      date: toUtcDateKey(bucketStart),
      label: UTC_SHORT_DATE_FORMATTER.format(bucketStart),
      intervalLabel: formatUtcDateRangeLabel(bucketStart, bucketEndExclusive),
    });
  }

  if (bucketAnchor === "end") {
    buckets.reverse();
  }

  return buckets;
}

function formatUtcDateRangeLabel(start: Date, endExclusive: Date): string {
  const normalizedStart = startOfUtcDay(start);
  const normalizedEndExclusive = startOfUtcDay(endExclusive);
  const endInclusive = addUtcDays(normalizedEndExclusive, -1);

  if (normalizedStart.getTime() === endInclusive.getTime()) {
    return UTC_SHORT_DATE_FORMATTER.format(normalizedStart);
  }

  return `${UTC_SHORT_DATE_FORMATTER.format(normalizedStart)} - ${UTC_SHORT_DATE_FORMATTER.format(endInclusive)}`;
}
